import { Percentile, DriverMetricsResponse } from "@/graphql/types/graphql";
import { DriverTableSegment } from "./driverTableUtils";
import { createDriverHeaders } from "./excel/createHeaders";
import { AnalysisDriverType } from "../types/types";
import { buildDriverRowData } from "./excel/buildRowData";
import { Cell, writeDriverWorksheet } from "./excel/writeWorkSheet";
import { EditedMetrics } from "../components/tables/DriverMetricsTableBody";

/**
 * Exports driver metrics data to an Excel file for a single driver based on headroomDriver.
 * @param segments - Array of driver table segments to export.
 * @param header1 - First header label (e.g., "Total Customer").
 * @param header2 - Second header label (e.g., "Total Prospect").
 * @param driver1Type - Type of the first driver or null.
 * @param driver2Type - Type of the second driver or null.
 * @param driver1Mode - Analysis mode for the first driver.
 * @param driver2Mode - Analysis mode for the second driver.
 * @param driver1NumeratorType - Numerator type for the first driver in percentage mode or null.
 * @param driver2NumeratorType - Numerator type for the second driver in percentage mode or null.
 * @param driver1Percentile - Percentile for driver 1.
 * @param driver2Percentile - Percentile for driver 2.
 * @param tableName - Base name for the exported Excel file.
 * @param segmentsToExport - Array of driver table segments to export (redundant, for compatibility).
 * @param data - Array of DriverMetricsResponse data from the backend.
 * @param sortColumn - Column to sort by.
 * @param sortDirection - Sorting direction.
 * @param editedPercentiles - Object containing edited percentile values.
 * @param headroomDriver - The selected driver ("driver1" or "driver2").
 */
export const exportDriverMetricsDataToExcel = (
  segments: DriverTableSegment[],
  header1: string,
  header2: string,
  driver1Type: string | null,
  driver2Type: string | null,
  driver1Mode: AnalysisDriverType,
  driver2Mode: AnalysisDriverType,
  driver1NumeratorType: string | null,
  driver2NumeratorType: string | null,
  driver1Percentile: Percentile,
  driver2Percentile: Percentile,
  tableName: string,
  segmentsToExport: DriverTableSegment[],
  data: DriverMetricsResponse[] | undefined,
  sortColumn: string,
  sortDirection: "asc" | "desc",
  editedPercentiles: EditedMetrics,
  headroomDriver: "driver1" | "driver2",
  headroomDriverPercentile: number
): void => {
  const segmentColumns = segments.length === 1 || segments.some((seg) => seg.method === "MANUAL") ? 2 : 2;
  const driverStartColumn = segmentColumns;
  const driverNumber = headroomDriver === "driver1" ? 1 : 2;
  const driverType = headroomDriver === "driver1" ? driver1Type : driver2Type;
  const driverMode = headroomDriver === "driver1" ? driver1Mode : driver2Mode;
  const driverNumeratorType = headroomDriver === "driver1" ? driver1NumeratorType : driver2NumeratorType;
  const driverPercentile = headroomDriver === "driver1" ? driver1Percentile : driver2Percentile;

  const headers = createDriverHeaders(
    segmentsToExport,
    driverNumber,
    driverType,
    driverMode,
    driverNumeratorType,
    headroomDriverPercentile,
    segmentColumns,
    driverStartColumn
  );

  const rowData = buildDriverRowData(
    segmentsToExport,
    data,
    driverMode,
    headroomDriver,
    sortColumn,
    sortDirection,
    editedPercentiles
  );

  const worksheetData: (string | number | Cell)[][] = [
    ...headers,
    ...rowData.map((row) => row.map((value) => ({ v: value }))),
  ];

  writeDriverWorksheet(worksheetData, segmentsToExport, segmentColumns, driverStartColumn, tableName);
};
